/**
 * Debug helpers for diagnosing why BridgeContext re-fires its change listeners.
 *
 * Flip `DEBUG_ENABLED` to `true` locally when you want to see the exact leaf-level
 * properties that triggered a context change. Leave it `false` on every commit.
 */

import type { BridgeContextChanges } from './bridgeContextChangeDetection';

const DEBUG_ENABLED = false;

export type PropDiffEntry = {
	next: unknown;
	path: string;
	prev: unknown;
};

/**
 * Returns true if `value` is a "plain" object — one we recurse into when diffing.
 * Library instances (Apollo client, Relay environment, etc.) have non-default
 * prototypes and are treated as opaque leaves to avoid traversing their internals.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
};

const diffValue = (prev: unknown, next: unknown, path: string, out: PropDiffEntry[]): void => {
	if (Object.is(prev, next)) {
		return;
	}

	if (Array.isArray(prev) && Array.isArray(next)) {
		const max = Math.max(prev.length, next.length);
		for (let i = 0; i < max; i++) {
			diffValue(prev[i], next[i], `${path}[${i}]`, out);
		}
		return;
	}

	if (isPlainObject(prev) && isPlainObject(next)) {
		const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
		for (const key of keys) {
			diffValue(prev[key], next[key], path ? `${path}.${key}` : key, out);
		}
		return;
	}

	// Mixed types, primitives, functions, or opaque (non-plain) objects: emit a leaf entry.
	out.push({ path, prev, next });
};

/**
 * Returns a flat list of `{path, prev, next}` entries describing every leaf-level difference
 * between `prev` and `next` under the named top-level keys. Recurses into plain objects and
 * arrays; treats functions, primitives, and non-plain objects (e.g. Apollo client) as opaque.
 */
export const diffProps = <T extends Record<string, unknown>>(
	prev: T,
	next: T,
	keys: readonly (keyof T & string)[],
): PropDiffEntry[] => {
	const out: PropDiffEntry[] = [];
	for (const key of keys) {
		diffValue(prev[key], next[key], key, out);
	}
	return out;
};

/**
 * Reports a BridgeContext change to the console. Emits the lightweight always-on top-level
 * summary, plus — when `DEBUG_ENABLED` is flipped on in this module — a deep
 * `{path, prev, next}` diff over the keys flagged as changed.
 *
 * Accepts the `BridgeContextChanges` record (not a pre-computed key list) so the
 * "what counts as a real change" rules — including the ignored-keys filter — live in
 * `computeBridgeContextChanges` only.
 */
export const logBridgeContextChange = <T extends Record<string, unknown>>(
	prev: T,
	next: T,
	changes: BridgeContextChanges,
): void => {
	if (!changes.__all__) {
		return;
	}
	const changedKeys = Object.keys(changes).filter(
		(k) => k !== '__all__' && changes[k as keyof BridgeContextChanges],
	) as (keyof T & string)[];

	// eslint-disable-next-line no-console
	console.log('BridgeContext changed:', changedKeys);

	if (!DEBUG_ENABLED) {
		return;
	}
	const diffs = diffProps(prev, next, changedKeys);
	// eslint-disable-next-line no-console
	console.log('BridgeContext deep diff:', diffs);
};
